import { Reducer } from 'redux';
import { ActionType, getType } from 'typesafe-actions';
import { PlayerActions } from './PlayerActions';
import { PlayerStateHelper } from './PlayerStateHelper';
import { FTUXType } from '../../Player/PlayerProperty/Progress/FTUXType';
import { BoosterType } from '../Card/BoosterType';
import { TimerType } from '../../System/Timer/TimerTypes';
import { freeSpinsOnUnlock } from '../../Constants/Economy';
import UIStateHelper from '../UI/UIStateHelper';
import { IGameState } from '../Game/IGameState';
import { InventoryStateHelper } from './Inventory/InventoryStateHelper';
import { LoginStateHelper } from './Login/LoginStateHelper';
import { QuestStateHelper } from './Quests/QuestStateHelper';
import { PetsStateHelper } from './Pets/PetsStateHelper';
import { IAPStateHelper } from './IAP/IAPStateHelper';
import { FieldStateHelper } from './Fields/FieldStateHelper';
import { SocialStateHelper } from './Social/SocialStateHelper';
import TimerStateHelper from '../Timer/TimerStateHelper';
import { FTUXStateHelper } from '../Game/FTUX/FTUXStateHelper';
import { PopupID } from '../UI/PopupID';
import { agreeWithTerms } from './Action/AgreeWithTerms';
import { RewardType } from 'Utility/Rewards/RewardType';
import { setFlag } from './PlayerFlagHelper';

export const PlayerReducer: Reducer<IGameState> = (
    state: IGameState,
    action: ActionType<typeof PlayerActions>
): IGameState => {
    state = PlayerStateHelper.setPlayerState(state, PlayerStateHelper.updateTimers(state.playerState));
    switch (action.type) {
        case getType(PlayerActions.agreeWithTerms):
            return agreeWithTerms(state);
        case getType(PlayerActions.openCrossPromoMenu):
            state = {
                ...state,
                playerState: PlayerStateHelper.updatePlayerState(state, {
                    ...state.playerState,
                    progress: {
                        ...state.playerState.progress,
                        xPromoWasShown: true,
                    },
                }),
            };
            return {
                ...state,
                UIState: UIStateHelper.showPopup(state.UIState, PopupID.CROSS_PROMO_MENU, action.payload),
                perSessionData: {
                    ...state.perSessionData,
                    hasClickedXPromoHUDButton: true,
                },
            };
        case getType(PlayerActions.setPlayerState):
            return {
                ...state,
                playerState: PlayerStateHelper.updatePlayerState(state, action.payload),
            };
        case getType(PlayerActions.updateTimers):
            return {
                ...state,
                playerState: PlayerStateHelper.updateTimers(state.playerState),
            };
        case getType(PlayerActions.receiveBoosterTNT):
            state = { ...state };
            state.playerState = InventoryStateHelper.setBoosterTNT(
                state.playerState,
                typeof state.playerState.inventory.boosterTNT !== 'number'
                    ? action.payload
                    : state.playerState.inventory.boosterTNT + action.payload
            );
            return state;
        case getType(PlayerActions.receiveBoosterBullsEye):
            state = { ...state };
            state.playerState = InventoryStateHelper.setBoosterBullsEye(
                state.playerState,
                typeof state.playerState.inventory[BoosterType.BULLS_EYE] !== 'number'
                    ? action.payload
                    : state.playerState.inventory[BoosterType.BULLS_EYE] + action.payload
            );
            return state;
        case getType(PlayerActions.receiveBoosterShuffle):
            state = { ...state };
            state.playerState = InventoryStateHelper.setBoosterShuffle(
                state.playerState,
                typeof state.playerState.inventory[BoosterType.SHUFFLE] !== 'number'
                    ? action.payload
                    : state.playerState.inventory[BoosterType.SHUFFLE] + action.payload
            );
            return state;
        case getType(PlayerActions.receiveBoosterUndo):
            state = { ...state };
            state.playerState = InventoryStateHelper.setBoosterUndo(
                state.playerState,
                typeof state.playerState.inventory[BoosterType.UNDO] !== 'number'
                    ? action.payload
                    : state.playerState.inventory[BoosterType.UNDO] + action.payload
            );
            return state;
        case getType(PlayerActions.receiveBoosterJoker):
            state = { ...state };
            state.playerState = InventoryStateHelper.setBoosterJoker(
                state.playerState,
                typeof state.playerState.inventory[BoosterType.JOKER] !== 'number'
                    ? action.payload
                    : state.playerState.inventory[BoosterType.JOKER] + action.payload
            );
            return state;
        case getType(PlayerActions.debugReceiveBoosterPlusFive):
            console.log('debugReceiveBoosterPlusFive');
            state = { ...state };
            state.playerState = InventoryStateHelper.setBoosterPlusFive(
                state.playerState,
                typeof state.playerState.inventory[BoosterType.PLUS_FIVE] !== 'number'
                    ? action.payload
                    : state.playerState.inventory[BoosterType.PLUS_FIVE] + action.payload
            );
            return state;
        case getType(PlayerActions.unlockSpinWheel):
            // Set initial spins and timer
            state = {
                ...state,
                playerState: InventoryStateHelper.setWheelSpins(state.playerState, freeSpinsOnUnlock),
            };
            state = TimerStateHelper.setTimer(state, TimerType.SPIN_WHEEL, null);
            // Add to completed FTUX
            return {
                ...state,
                playerState: FTUXStateHelper.updateFTUXProgress(state.playerState, [FTUXType.SPIN_WHEEL]),
            };
        case getType(PlayerActions.spinWheel):
            return {
                ...state,
                playerState: PlayerStateHelper.spinWheel(state.playerState, action.payload),
            };
        case getType(PlayerActions.clearTasks):
            return PlayerStateHelper.clearTasks(state, action.payload);
        case getType(PlayerActions.setVolume):
            return PlayerStateHelper.setAudio(state, action.payload);
        case getType(PlayerActions.receiveCurrencyShareReward):
            return PlayerStateHelper.receiveCurrencyShareReward(state, action.payload);
        case getType(PlayerActions.receiveFriendGiftReward):
            return PlayerStateHelper.receiveFriendGiftReward(state, action.payload);
        case getType(PlayerActions.unlockFields):
            state = FieldStateHelper.unlockFields(state, ...action.payload);
            return state;
        case getType(PlayerActions.receiveRewards):
            return {
                ...state,
                playerState: InventoryStateHelper.receiveRewards(state.playerState, action.payload.rewards, {
                    dontTrack: action.payload.dontTrack,
                    queueTask: action.payload.queueTask,
                    groupTasks: action.payload.groupTasks,
                    rewardFrom: action.payload.rewardFrom,
                    queueNext: action.payload.queueNext,
                }),
            };

        case getType(PlayerActions.setFTUXUnlocks):
            return {
                ...state,
                playerState: FTUXStateHelper.updateFTUXProgress(state.playerState, action.payload.unlocks),
            };
        case getType(PlayerActions.clearFTUX):
            return FTUXStateHelper.clearFTUXProgress(state, ...action.payload);
        case getType(PlayerActions.clearField):
            return FieldStateHelper.clearField(state, action.payload);
        case getType(PlayerActions.addTasks):
            const tasks = action.payload;
            return {
                ...state,
                playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, tasks),
            };
        case getType(PlayerActions.addFTUETrackingEvent):
            return PlayerStateHelper.addFBTrackingEvent(state, action.payload);
        case getType(PlayerActions.completeWheelTimer):
            return PlayerStateHelper.completeWheelTimer(state);
        case getType(PlayerActions.claimShopGift):
            return PlayerStateHelper.claimShopGift(state);
        case getType(PlayerActions.completeShopGiftTimer):
            return PlayerStateHelper.completeShopGiftTimer(state);
        case getType(PlayerActions.completeAdCooldownTimer):
            return PlayerStateHelper.completeAdCooldownTimer(state);
        case getType(PlayerActions.claimQuestReward):
            return {
                ...state,
                playerState: QuestStateHelper.claimQuestReward(state, action.payload),
            };
        case getType(PlayerActions.claimQuestRewardById):
            return QuestStateHelper.claimQuestRewardById(state, action.payload);
        case getType(PlayerActions.refreshQuestAfterCooldown):
            return QuestStateHelper.refreshQuestAfterCooldown(state, action.payload);
        case getType(PlayerActions.completeLevel):
            return PlayerStateHelper.levelComplete(state);
        case getType(PlayerActions.showLevelAutoFlowPopup):
            return PlayerStateHelper.showLevelAutoFlowPopup(state, action.payload);
        case getType(PlayerActions.showSundayOfferPopup):
            return IAPStateHelper.showSundayOfferPopup(state);
        case getType(PlayerActions.showStarterPackOfferPopup):
            return IAPStateHelper.showStarterPackOfferPopup(state);
        case getType(PlayerActions.showPayerReactivationPopup):
            return IAPStateHelper.showPayerReactivationPopup(state);
        case getType(PlayerActions.showOfferPopups):
            return IAPStateHelper.showOfferPopups(state, action.payload);
        case getType(PlayerActions.harvest):
            return FieldStateHelper.harvest(state, { playAutoFlow: action.payload });
        case getType(PlayerActions.claimDailyLoginReward):
            const newState = LoginStateHelper.claimDailyLoginRewards(state, action.payload);
            return {
                ...newState,
                UIState: UIStateHelper.hidePopup(newState.UIState, PopupID.DAILY_LOGIN_MENU),
            };
        case getType(PlayerActions.updateLoginStreak):
            return LoginStateHelper.setLoginStreak(state, action.payload);
        case getType(PlayerActions.setPetLevel):
            return PlayerStateHelper.setPlayerState(
                state,
                PetsStateHelper.setPetLevel(state.playerState, action.payload)
            );
        case getType(PlayerActions.setBotSubscribed):
            return PlayerStateHelper.setPlayerState(state, {
                ...state.playerState,
                botOptIn: action.payload,
            });
        case getType(PlayerActions.setCreatedShortcut):
            return PlayerStateHelper.setPlayerState(state, {
                ...state.playerState,
                hasShortcut: true,
            });
        case getType(PlayerActions.setDeviceId):
            return PlayerStateHelper.setDeviceId(state, action.payload);
        case getType(PlayerActions.setFacebookUserId):
            return PlayerStateHelper.setFacebookUserId(state, action.payload);
        case getType(PlayerActions.setCurrentEventOffer):
            return {
                ...state,
                playerState: {
                    ...state.playerState,
                    currentEventOffer: action.payload,
                },
            };
        case getType(PlayerActions.setFlag):
            return {
                ...state,
                playerState: setFlag(state.playerState, action.payload.id, action.payload.expiryDate),
            };
        case getType(PlayerActions.resetFlags):
            return {
                ...state,
                playerState: {
                    ...state.playerState,
                    flags: [],
                },
            };
    }

    return state;
};
